import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Radio, Clock3, Loader2, ChartNoAxesCombined, Volleyball } from "lucide-react";
import { matchesApi, stableSig } from "../lib/api";
import { getCachedMatches, setCachedMatches } from "../lib/matchListCache";
import { TEAMS } from "../lib/site";
import MatchMarkets from "../components/MatchMarkets";
import NewsTicker from "../components/NewsTicker";
import SportIconRow from "../components/SportIconRow";
import HeroCarousel from "../components/HeroCarousel";
import { useSportsbookLauncher } from "../components/SportsBook";

const TABS = [
  { key: "highlights", label: "Highlights" },
  { key: "inplay", label: "In-Play" },
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "result", label: "Result" },
];

const PROMO_SLIDES = [
  { key: "premium", img: "/img/sports.webp", badge: "Premium Cricket", badgeClass: "bg-[#f5b400] text-black", title: "Live Odds · Session · Lambi", subtitle: "Lagai & Khai on every match" },
  { key: "deposit", to: "/wallet?tab=deposit", img: "/img/depositWithdraw.webp", badge: "Fast & Secure", badgeClass: "bg-[#E0B84C] text-black", title: "Instant Deposits & Withdrawals", cta: "Add Money" },
  { key: "weekly", to: "/wallet", img: "/img/weeklyBonus.webp", badge: "Weekly Reward", badgeClass: "bg-emerald-500 text-black", title: "Weekly Losing Bonus", subtitle: "Cashback on your weekly losses" },
];

const istDay = (offset = 0) => new Date(Date.now() + (330 + offset * 1440) * 60000).toISOString().slice(0, 10);

function BatBall({ className, live = false }) {
  // Live: golden bat + red ball. Not live: inherits the muted grey via currentColor.
  const bat = live ? "#F5B81C" : "currentColor";
  const ball = live ? "#E02424" : "currentColor";
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-live={live ? "1" : "0"}>
      <path d="M14.5 3.5l6 6-9 9-3-3z" stroke={bat} fill={live ? "#F5B81C" : "none"} fillOpacity={live ? 0.35 : 0} />
      <path d="M11.5 15.5l-4 4-3-3 4-4" stroke={bat} />
      <circle cx="6" cy="7" r="2.5" stroke={ball} fill={live ? "#E02424" : "none"} />
    </svg>
  );
}

const teamName = (m, side) => m[`${side}Team`]?.name || TEAMS[m[side]]?.name || m[side];
const titleOf = (m) => `${teamName(m, "home")} v ${teamName(m, "away")}`;

function whenParts(m) {
  if (!m.startTime) return m.matchDateStr ? { date: m.matchDateStr, time: m.matchTimeStr || "" } : { date: "Scheduled", time: "" };
  const d = new Date(m.startTime);
  const date = d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }).replace(",", "");
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }).toUpperCase();
  return { date, time };
}

// Colour-coded schedule label: gold date · green time (international "Sat 11 Oct 2026 · 09:20 AM" style).
function When({ m }) {
  const { date, time } = whenParts(m);
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold" data-testid={`sports-when-${m.id}`}>
      <span className="text-[#b8860b]">{date}</span>
      {time && <><span className="text-slate-300">·</span><span className="inline-flex items-center gap-1 text-[#15803d]"><Clock3 className="h-3 w-3" />{time}</span></>}
    </span>
  );
}

function Badge({ k }) {
  return <span className="grid place-items-center h-[15px] w-[15px] rounded-[3px] bg-[#1e5bd6] text-white text-[9px] font-extrabold leading-none">{k}</span>;
}

function Row({ m, last }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const live = m.status === "live";
  const done = m.status === "completed";
  return (
    <div data-testid={`sports-row-${m.id}`} className={`bg-white ${last ? "" : "border-b border-slate-200"}`}>
      <div className="flex items-center gap-2.5 pl-3 pr-2 py-2.5">
        <button onClick={() => setOpen((v) => !v)} data-testid={`sports-toggle-${m.id}`} className="flex items-center gap-2.5 min-w-0 flex-1 text-left">
          <BatBall live={live} className="h-5 w-5 text-slate-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[12px] text-slate-500 font-medium">
              <Badge k="F" /><Badge k="B" />
              {live ? (
                <span className="inline-flex items-center gap-1 text-[#1e5bd6] font-bold" data-testid={`sports-inplay-${m.id}`}>
                  <Radio className="h-3 w-3" /> In-Play
                </span>
              ) : <When m={m} />}
            </div>
            <div className="mt-0.5 font-bold text-[15px] text-[#1f5fd0] truncate">{titleOf(m)}</div>
            {done && m.result && <div className="text-[11px] text-emerald-700 font-semibold truncate">{m.result}</div>}
          </div>
          {open ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : null}
        </button>
        <button onClick={() => navigate(`/match/${m.id}`)} aria-label="Open match" data-testid={`sports-open-${m.id}`}
          className="grid place-items-center h-6 w-6 rounded-full bg-[#2d6fdc] text-white shrink-0 active:scale-95 transition-transform">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      {open && !done && (
        <div className="px-3 sm:px-4 pb-4 pt-1 bg-slate-100" data-testid={`sports-expand-${m.id}`}>
          <MatchMarkets match={m} />
        </div>
      )}
    </div>
  );
}

function FeaturedBanner({ m }) {
  if (!m) return null;
  return (
    <div className="relative h-28 overflow-hidden" data-testid="sports-featured-banner">
      <img src="/img/sports.webp" alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
      <div className="relative h-full px-4 flex flex-col justify-center">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#f5b400]">{m.series || "Featured Match"}</span>
        <span className="font-display font-extrabold text-lg text-white leading-tight">{teamName(m, "home")}</span>
        <span className="font-display font-extrabold text-lg text-white leading-tight">v {teamName(m, "away")}</span>
      </div>
    </div>
  );
}

export default function Sports() {
  const [matches, setMatches] = useState(() => getCachedMatches()); // instant paint from last snapshot
  const [tab, setTab] = useState("highlights");
  const sportsbook = useSportsbookLauncher();

  useEffect(() => {
    let lastSig = stableSig(getCachedMatches());
    const load = () => matchesApi.list().then((data) => {
      setCachedMatches(data);
      const sig = stableSig(data);
      if (sig !== lastSig) { lastSig = sig; setMatches(data); }
    }).catch(() => {});
    load();
    const t = setInterval(load, 120000); // match LIST only — detail page keeps its live odds/session stream
    return () => clearInterval(t);
  }, []);

  const liveCount = matches.filter((m) => m.status === "live").length;
  const list = useMemo(() => {
    const today = istDay(0), tomorrow = istDay(1);
    return matches
      .filter((m) => {
        if (tab === "result") return m.status === "completed";
        if (m.status === "completed") return false;
        if (tab === "inplay") return m.status === "live";
        if (tab === "today") return m.status === "live" || m.matchDay === today;
        if (tab === "tomorrow") return m.matchDay === tomorrow;
        return true;
      })
      .sort((a, b) => tab === "result"
        ? String(b.startTime || "").localeCompare(String(a.startTime || ""))
        : (a.status === "live" ? 0 : 1) - (b.status === "live" ? 0 : 1) ||
          String(a.startTime || "").localeCompare(String(b.startTime || "")));
  }, [matches, tab]);

  const featured = matches.find((m) => m.status === "live") || null;

  const Toggle = (
    <div className="bg-[#0b0b0b] px-3 py-2" data-testid="sports-mode-toggle" role="tablist">
      <div className="relative h-12 rounded-full overflow-hidden grid grid-cols-2 bg-[#15181f] ring-2 ring-[#f5b400] shadow-[0_0_18px_rgba(245,180,0,0.45),inset_0_1px_0_rgba(255,255,255,0.18)]">
        <button type="button" role="tab" aria-selected data-testid="sports-mode-exchange"
          className="relative z-10 inline-flex items-center justify-center gap-2 text-[15px] font-extrabold text-black bg-gradient-to-b from-[#ffd84a] via-[#f5b400] to-[#e3a300] [clip-path:polygon(0_0,100%_0,calc(100%-14px)_100%,0_100%)] -mr-[14px]">
          <ChartNoAxesCombined className="h-5 w-5" strokeWidth={2.5} /> Exchange
        </button>
        <button type="button" role="tab" aria-selected={false} onClick={sportsbook.open} disabled={sportsbook.opening || !sportsbook.enabled} data-testid="sports-mode-sportsbook"
          className="inline-flex items-center justify-center gap-2 pl-3 text-[15px] font-extrabold text-white bg-gradient-to-b from-[#232833] to-[#101318] hover:from-[#2b313d] transition-colors disabled:opacity-60 active:scale-[0.98]">
          {sportsbook.opening ? <Loader2 className="h-5 w-5 animate-spin text-[#f5b400]" /> : <Volleyball className="h-5 w-5" strokeWidth={2.2} />}
          {sportsbook.enabled ? "Sportsbook" : "Sportsbook (soon)"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto sm:px-6 sm:py-5" data-testid="sports-page">
      <NewsTicker />
      <div className="bg-[#0b0b0b]"><HeroCarousel slides={PROMO_SLIDES} /></div>
      <SportIconRow liveCount={liveCount} />

      {Toggle}

      <div className="grid grid-cols-5 bg-[#1c2733] border-y border-[#2b3a4a]" data-testid="sports-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sports-tab-${t.key}`}
            className={`py-2.5 text-[12px] font-semibold border-r border-[#2b3a4a] last:border-r-0 transition-colors ${
              tab === t.key ? "bg-[#3b4a5a] text-white" : "text-slate-300 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-[#2b2f36] text-white text-center text-sm font-semibold py-2" data-testid="sports-highlights-bar">
        {TABS.find((t) => t.key === tab)?.label}
      </div>
      {tab === "highlights" && <FeaturedBanner m={featured} />}

      <div className="bg-white overflow-hidden shadow-lg sm:rounded-b-xl">
        {list.length === 0
          ? <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="sports-empty">
              {tab === "result" ? "No results yet." : "No matches in this section right now."}
            </div>
          : list.map((m, i) => <Row key={m.id} m={m} last={i === list.length - 1} />)}
      </div>
    </div>
  );
}
